import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import CoachTodoList from "./CoachTodoList";
import MatchStatusPill from "../Controls/MatchStatusPill";
import API from "@src/API";
import "../../style/scss/Recruiter.scss";

function pick(obj, name) {
  if (!obj) return undefined;
  return obj[name] ?? obj[name.charAt(0).toLowerCase() + name.slice(1)];
}

export default function EmployerDashboard() {
  const { id } = useParams();
  const employerId = Number(id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!employerId) return;
    setLoading(true);
    try {
      const resp = await API.getAsync(`/Recruiter/Employer/${employerId}`);
      if (resp?.ok) {
        setData(resp.jsonData);
        setError("");
      } else if (resp?.status === 403) {
        setError("You do not have access to this company.");
      } else {
        setError("Could not load this company.");
      }
    } catch {
      setError("Could not load this company.");
    } finally {
      setLoading(false);
    }
  }, [employerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const jobs = pick(data, "Jobs") || [];
  const name = pick(data, "EmployerName") || "Company";

  return (
    <div className="pagebody recruiter-dashboard">
      <BreadCrumb
        props={[
          { label: "Hiring", route: "/recruiter" },
          { label: name, route: `/company/${employerId}` },
        ]}
      />
      <div className="tabbody">
        <div className="recruiter-dashboard__hero">
          <div>
            <h2>{name}</h2>
            <p className="byline">Every active opening, with how far its candidates have moved.</p>
          </div>
          <div className="recruiter-dashboard__stats">
            <div>
              <span className="stat">{pick(data, "ActiveJobCount") ?? 0}</span>
              <span className="statLabel">open roles</span>
            </div>
            <div>
              <span className="stat">{pick(data, "MatchCount") ?? 0}</span>
              <span className="statLabel">matches</span>
            </div>
            <div>
              <span className="stat">{pick(data, "InProcessCount") ?? 0}</span>
              <span className="statLabel">in process</span>
            </div>
          </div>
        </div>

        {error ? <p className="recruiter-dashboard__error">{error}</p> : null}
        {loading && !data ? <p className="byline">Loading openings…</p> : null}

        <CoachTodoList todos={pick(data, "Todos") || []} title="Fastest wins for this company" />

        <div className="tabsection">
          <div className="listTitle">
            <span>Open roles</span>
          </div>
          {jobs.length === 0 ? (
            <p className="byline">
              No active openings. <Link to="/employerRecruitEdit">Post one</Link> and matching starts
              on its own.
            </p>
          ) : (
            <ul className="recruiter-job-list">
              {jobs.map((j) => {
                const jobId = pick(j, "JobID");
                const opportunity = pick(j, "TopOpportunity");
                return (
                  <li key={jobId}>
                    <div className="recruiter-job-list__head">
                      <Link to={`/jobWorkspace/${jobId}`}>{pick(j, "Title")}</Link>
                      <MatchStatusPill status={pick(j, "MatchStatus")} />
                    </div>
                    <div className="recruiter-job-list__counts">
                      <span>{pick(j, "MatchCount") ?? 0} matched</span>
                      <span>{pick(j, "InProcessCount") ?? 0} in process</span>
                      <span>{pick(j, "SkillCount") ?? 0} required skills</span>
                    </div>
                    {opportunity ? <div className="byline">{opportunity}</div> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
